import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { SelectDropdown } from '@makyoapp/makyo-ui'

import { useCacheContext } from '@/context/CacheContext'
import { useDynamicFontLoading } from '@/hooks/useDynamicFontLoading'
import { useFontCache } from '@/hooks/useFontCache'

import { FontSelectProps } from './FontSelect'

function OptionFontPreview({ family }: { family: string }) {
  const { loadGoogleFont } = useDynamicFontLoading()
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    let done = false

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!done && entry.isIntersecting) {
          done = true
          loadGoogleFont(family)
          io.disconnect()
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px',
      }
    )
    io.observe(el)
    return () => {
      io.disconnect()
    }
  }, [family, loadGoogleFont])

  return (
    <div ref={ref} style={{ fontFamily: family }}>
      {family}
    </div>
  )
}

export function FontSelectOptimized({ id, value, onChange }: FontSelectProps) {
  const { allFonts, loadAllFonts } = useCacheContext()
  const { loadGoogleFont } = useDynamicFontLoading()
  const { cacheFont } = useFontCache()
  const [hasLoadedAllFonts, setHasLoadedAllFonts] = useState(false)

  const [loadedFonts, setLoadedFonts] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (!hasLoadedAllFonts && allFonts.length <= 100) {
      loadAllFonts()
      setHasLoadedAllFonts(true)
    }
  }, [hasLoadedAllFonts, allFonts.length, loadAllFonts])

  // Enhanced loadGoogleFont with tracking
  const trackLoadGoogleFont = useCallback(
    (fontFamily: string, reason: 'visible' | 'selected') => {
      if (!loadedFonts.has(fontFamily)) {
        loadGoogleFont(fontFamily)
        setLoadedFonts((prev) => new Set([...prev, fontFamily]))
      }
    },
    [loadGoogleFont, loadedFonts]
  )

  const handleFontSelect = useCallback(
    (fontFamily: string) => {
      trackLoadGoogleFont(fontFamily, 'selected')
      cacheFont(fontFamily)
      onChange(fontFamily)
    },
    [trackLoadGoogleFont, cacheFont, onChange]
  )

  const selected = useMemo(() => {
    return allFonts.find((font) => font.family === value)
  }, [allFonts, value])

  return (
    <SelectDropdown
      id={id}
      windowed
      value={selected}
      withSearch
      menuDefaultSize={false}
      menuContainerClassName='min-w-[200px]'
      onChange={(opt) => handleFontSelect(opt.family)}
      options={allFonts}
      optionLabel={(opt) => opt.family}
      renderOption={(opt) => (
        <div>
          <OptionFontPreview family={opt.family} />
        </div>
      )}
    />
  )
}
